// Reload the app from scratch, past every cache between here and the server.
//
// WHY A BUTTON FOR THIS. `window.location.reload()` is a SOFT refresh: the
// webview is free to answer it out of its own HTTP cache, and for index.html —
// the one file whose name never changes, and the one that names the current
// hashed bundles — it usually does. So the app "reloads" and comes back on
// exactly the build it was already running. That is what makes people ask for
// this button in the first place; a menu item that only calls reload() would
// reproduce the bug it was added to fix.
//
// The way past it is a URL the cache has never seen. A changed query string is
// a different resource as far as any HTTP cache is concerned, so index.html is
// fetched fresh, and it names the current bundles.
//
// ⚠️ THE QUERY GOES IN THE SEARCH, NOT THE HASH. This app uses HashRouter
// (App.jsx), so the ROUTE lives in the hash: `…/?r=123#/home`. Putting the
// cache-buster after the '#' would change the route instead of the URL the
// cache keys on — the page would navigate and never reload. Keeping the hash
// intact is also what puts the user back on the screen they were looking at.
//
// ── What this does NOT do, deliberately ────────────────────────────────────
//
//   It does NOT log out. The session lives in localStorage and is left alone;
//   someone refreshing a stale screen does not want to find the PIN pad.
//
//   It does NOT clear the offline SQLite cache. That is minutes of re-sync on a
//   slow connection to fix a problem that is almost never the cache — and on a
//   phone with no signal it would delete the only data the app still has.
//
//   It does NOT force a pending OTA bundle to apply. Reloading the webview is
//   not a cold start, and otaClient stages bundles with next() rather than set()
//   on purpose, so the screen is never yanked out from under whoever is using
//   it. On native this reload therefore restarts the JS on the SAME bundle: it
//   clears in-memory state, reconnects MQTT and re-runs every fetch, which is
//   what "the app is showing something stale" usually needs. A new bundle still
//   arrives on the next real restart.

/** Any Cache Storage entries — left behind by a service worker from an older build. */
async function clearCacheStorage() {
  try {
    if (typeof caches === 'undefined') return;
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  } catch (e) {
    // Not available in every context, and never worth failing the refresh over.
  }
}

/**
 * Any registered service worker.
 *
 * This app registers none (src/index.js), but a build that once did leaves a
 * worker installed in every browser that visited it, and that worker keeps
 * serving its own cached index.html to a URL it has already seen — outliving
 * the code that created it. Unregistering costs nothing when there is none.
 */
async function unregisterServiceWorkers() {
  try {
    if (!navigator.serviceWorker) return;
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
  } catch (e) {
    // ignore
  }
}

/** The current URL with a fresh cache-buster, route (hash) untouched. */
export function bustedUrl(href, stamp) {
  const url = new URL(href);
  url.searchParams.set('r', String(stamp));
  return url.toString();
}

/**
 * Clear what can be cleared, then reload on a URL no cache has seen.
 *
 * Never returns in practice — the document navigates away.
 */
export async function hardRefresh() {
  await clearCacheStorage();
  await unregisterServiceWorkers();
  // replace(), not assign(): the pre-refresh entry is the same screen, and
  // leaving it in history makes Back look like it undid the refresh.
  window.location.replace(bustedUrl(window.location.href, Date.now()));
}
